/**
 * Coordinate-space conversions for the document viewer.
 *
 * The canonical overlay coordinate space is CSS pixels at scale=1 and
 * rotation=0 — i.e. the page's intrinsic dimensions.
 *
 * The "rendered view" space is CSS pixels in the on-screen page container
 * (after scale + rotation). Rotation is applied first, then scale, with
 * the rotated origin pinned to the rendered container's top-left.
 *
 * Rotation conventions:
 *   - 0°   : identity
 *   - 90°  : clockwise, intrinsic top edge ends up on the right
 *   - 180° : flips both axes
 *   - 270° : counter-clockwise (= 90° CCW), intrinsic top edge ends up on
 *            the left
 */

import type { PageDimensions, Rotation, RenderedPageSize } from './types'

export interface Point {
  x: number
  y: number
}

export interface Rect {
  x: number
  y: number
  width: number
  height: number
}

/**
 * Outer container size of a rendered page (CSS pixels) after applying
 * scale and rotation. 90°/270° swap width and height.
 */
export function renderedPageSize(
  dims: PageDimensions,
  scale: number,
  rotation: Rotation,
): RenderedPageSize {
  const rotated = rotation === 90 || rotation === 270
  const w = rotated ? dims.height : dims.width
  const h = rotated ? dims.width : dims.height
  return { width: w * scale, height: h * scale }
}

/**
 * Map a point from document space (intrinsic CSS pixels) → rendered view
 * space (CSS pixels relative to the page container's top-left).
 */
export function documentToView(
  point: Point,
  dims: PageDimensions,
  scale: number,
  rotation: Rotation,
): Point {
  const { x, y } = point
  const { width: W, height: H } = dims
  switch (rotation) {
    case 0:
      return { x: x * scale, y: y * scale }
    case 90:
      return { x: (H - y) * scale, y: x * scale }
    case 180:
      return { x: (W - x) * scale, y: (H - y) * scale }
    case 270:
      return { x: y * scale, y: (W - x) * scale }
  }
}

/**
 * Inverse of `documentToView`. Maps rendered-view coordinates (e.g. a
 * pointer event relative to the page container) back into document
 * coordinates that overlay data is stored against.
 */
export function viewToDocument(
  point: Point,
  dims: PageDimensions,
  scale: number,
  rotation: Rotation,
): Point {
  const vx = point.x / scale
  const vy = point.y / scale
  const { width: W, height: H } = dims
  switch (rotation) {
    case 0:
      return { x: vx, y: vy }
    case 90:
      // forward: x' = (H - y)s, y' = xs  →  x = vy, y = H - vx
      return { x: vy, y: H - vx }
    case 180:
      return { x: W - vx, y: H - vy }
    case 270:
      // forward: x' = ys, y' = (W - x)s  →  x = W - vy, y = vx
      return { x: W - vy, y: vx }
  }
}

/**
 * Map an axis-aligned rectangle from document space → rendered view
 * space. The rectangle stays axis-aligned for 0° / 180°. For 90° / 270°
 * its width and height swap.
 */
export function documentRectToView(
  rect: Rect,
  dims: PageDimensions,
  scale: number,
  rotation: Rotation,
): Rect {
  // Convert opposite corners then normalize, so the result is always a
  // canonical (x, y, w, h) rectangle regardless of which corner mapped
  // where after rotation.
  const a = documentToView({ x: rect.x, y: rect.y }, dims, scale, rotation)
  const b = documentToView(
    { x: rect.x + rect.width, y: rect.y + rect.height },
    dims,
    scale,
    rotation,
  )
  const x = Math.min(a.x, b.x)
  const y = Math.min(a.y, b.y)
  return {
    x,
    y,
    width: Math.abs(b.x - a.x),
    height: Math.abs(b.y - a.y),
  }
}

/**
 * Compute the zoom factor that fits a page into the given viewport.
 *
 * - `width`: scale so the rendered width matches `viewport.width`
 * - `page` : scale so both width and height fit inside the viewport
 *
 * `padding` shaves a few CSS pixels off each axis so the page never
 * touches the scroll-container edge (and so the horizontal scrollbar
 * doesn't immediately appear at fit-to-width).
 */
export function computeFitScale(args: {
  dims: PageDimensions
  rotation: Rotation
  viewport: { width: number; height: number }
  mode: 'page' | 'width'
  padding?: number
}): number {
  const { dims, rotation, viewport, mode, padding = 16 } = args
  const rotated = rotation === 90 || rotation === 270
  const pageW = rotated ? dims.height : dims.width
  const pageH = rotated ? dims.width : dims.height
  const availW = Math.max(1, viewport.width - padding * 2)
  const availH = Math.max(1, viewport.height - padding * 2)
  if (mode === 'width') {
    return availW / pageW
  }
  return Math.min(availW / pageW, availH / pageH)
}

/** Clamp a zoom factor to safe display bounds. */
export function clampScale(scale: number, min = 0.1, max = 8): number {
  if (!Number.isFinite(scale)) return 1
  return Math.min(max, Math.max(min, scale))
}
